import { useState, useCallback, useEffect } from 'react'
import { Bookmark, Clock, RotateCcw } from 'lucide-react'
import type { HistorySnapshot } from '@/pages/admin/cms-editor/types'

/* ── Props ───────────────────────────────────────────── */

interface HistoryPanelProps {
  snapshots: HistorySnapshot[]
  currentIndex: number
  onRestore: (index: number) => void
  onNameSnapshot: (id: string, name: string) => void
}

/* ── Component ───────────────────────────────────────── */

export default function HistoryPanel({
  snapshots,
  currentIndex,
  onRestore,
  onNameSnapshot,
}: HistoryPanelProps) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')

  // Tick every 30 s so the "X mins ago" labels stay fresh
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [])

  const startRename = useCallback((snap: HistorySnapshot) => {
    setEditingId(snap.id)
    setEditName(snap.name)
  }, [])

  const commitRename = useCallback(() => {
    if (editingId && editName.trim()) {
      onNameSnapshot(editingId, editName.trim())
    }
    setEditingId(null)
  }, [editingId, editName, onNameSnapshot])

  const formatTime = (ts: number) => {
    const d = new Date(ts)
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  }

  const timeSince = (ts: number) => {
    const diff = now - ts
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    return `${hrs}h ago`
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Section header */}
      <div className="editor-section-header" style={{ cursor: 'default' }}>
        <Clock size={12} />
        <span style={{ flex: 1 }}>HISTORY</span>
        <span className="editor-badge">{snapshots.length}</span>
      </div>

      {/* Timeline */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
        {snapshots.length === 0 && (
          <div className="editor-empty-state">
            <RotateCcw size={24} className="editor-empty-state-icon" />
            <div className="editor-empty-state-text">No history yet</div>
            <div className="editor-empty-state-hint">Changes will appear here as you edit</div>
          </div>
        )}

        {snapshots.map((snap, i) => {
          const isCurrent = i === currentIndex
          const isFuture = i > currentIndex

          return (
            <div
              key={snap.id}
              className="editor-history-item"
              data-current={isCurrent}
              data-future={isFuture}
              onClick={() => onRestore(i)}
            >
              {/* Timestamp */}
              <span className="editor-history-time" title={formatTime(snap.timestamp)}>
                {timeSince(snap.timestamp)}
              </span>

              {/* Label (editable on double-click) */}
              {editingId === snap.id ? (
                <input
                  className="editor-input editor-input-sm"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  onBlur={commitRename}
                  onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setEditingId(null) }}
                  style={{ flex: 1 }}
                />
              ) : (
                <span
                  className="editor-history-label"
                  onDoubleClick={() => startRename(snap)}
                  title="Double-click to rename"
                >
                  {snap.name}
                </span>
              )}

              {/* Bookmark button */}
              <button
                className="editor-btn editor-btn-ghost editor-btn-icon editor-btn-sm editor-history-snapshot"
                onClick={e => { e.stopPropagation(); startRename(snap) }}
                title="Name this snapshot"
              >
                <Bookmark size={10} />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
